import { type ZodRawShape, z } from "zod";

import { type TKiliDataTableDefinition } from "@/data/api/kili/common/datatable/data-table-definition";
import { nonEmptyArray } from "@/utils/array";

export enum EKiliInvalidFilterErrorCode {
  UNKNOWN_COLUMN = "UNKNOWN_COLUMN",
  INVALID_VALUE = "INVALID_VALUE",
  UNEXPECTED_FILTER_ERROR = "UNEXPECTED_FILTER_ERROR",
}

export enum EKiliSortFilterDirection {
  ASC = "ASC",
  DESC = "DESC",
}

// eslint-disable-next-line
export function kiliDataTableSearchRequestBody<T extends TKiliDataTableDefinition<any>>(dataTableDefinition: T) {
  return kiliDataTableExportRequestBody(dataTableDefinition).extend({
    pageSize: z.number(),
    page: z.number(),
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function kiliDataTableExportRequestBody<T extends TKiliDataTableDefinition<any>>(dataTableDefinition: T) {
  const filtersSchema = nonEmptyArray(
    Object.values(dataTableDefinition.filterableFields).map((field) => {
      return z.object({
        name: z.literal(field.key),
        value: z.any(),
        operator: z.string(),
      });
    }),
  );

  if (dataTableDefinition.additionalFilters) {
    Object.values(dataTableDefinition.additionalFilters).forEach((field) => {
      filtersSchema.push(
        z.object({
          name: z.literal(field.key),
          value: z.any(),
          operator: z.string(),
        }),
      );
    });
  }

  const filters = z.array(z.discriminatedUnion("name", [...filtersSchema]));

  const sortedFields = Object.keys(dataTableDefinition.headers).filter(
    (key) => dataTableDefinition.headers[key].sortable,
  );

  if (sortedFields.length) {
    const sortsSchema = nonEmptyArray(
      sortedFields.map((key) => {
        return z.object({
          fieldName: z.literal(key),
          direction: z.nativeEnum(EKiliSortFilterDirection),
        });
      }),
    );

    const sorts = z.array(z.discriminatedUnion("fieldName", [...sortsSchema]));

    return z.object({
      filters,
      sorts,
    });
  }

  return z.object({
    filters,
    sorts: z.array(z.never()), // should be an empty array
  });
}

export function kiliDataTableRequestResponse<T extends ZodRawShape>(schema: z.ZodObject<T>) {
  return z.object({
    rows: z.array(schema),
    totalRows: z.number(),
    invalidFilters: z.array(
      z.object({
        name: z.string(),
        message: z.string(),
        code: z.nativeEnum(EKiliInvalidFilterErrorCode),
      }),
    ),
  });
}

export type TKiliDataTableFilter = {
  name: string;
  // eslint-disable-next-line
  value: any;
  // eslint-disable-next-line
  operator: any;
};

export type TKiliDataTableSort = {
  fieldName: string;
  direction: EKiliSortFilterDirection;
};

export type TKiliDataTableExportRequestBody = {
  filters: Array<TKiliDataTableFilter>;
  sorts: Array<TKiliDataTableSort>;
};

export type TKiliDataTableRequestParams = Record<string, number>;

export interface IKiliDataTableSearchRequestBody extends TKiliDataTableExportRequestBody {
  pageSize: number;
  page: number;
  params?: TKiliDataTableRequestParams;
}

export type TKiliDataTableRequestResponse<SCHEMA> =
  // eslint-disable-next-line
  SCHEMA extends z.ZodObject<any>
    ? {
        rows: Array<z.infer<SCHEMA>>;
        totalRows: number;
        invalidFilters: Array<{
          name: string;
          message: string;
          code: EKiliInvalidFilterErrorCode;
        }>;
      }
    : never;
